import {
  ChevronDown,
  ChevronUp,
  DataTable,
  DataTableReference,
  ArrowUpRight,
} from '@carbon/react/icons';

import style from './calculation-details.module.scss';
import { prepareData } from './utils';
import type {
  InterestCalculationDetails,
  StickKeys,
  TabularCalculationDetails,
} from 'types/title-calculations/types';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { type ReactNode, useEffect, useReducer, useState } from 'react';
import { InterestMath } from './interest-math';
import { InterpretationDrawer } from 'components/interpretation-view/interpretation-view';
import { Link } from 'components/link';
import type { HighlightRunsheetRowProps } from 'lib/types';
import { useCreateInterpretationCalculationDetailsAtom } from './atoms';
import { useAtomValue } from 'jotai';
import type { TitleCalculationDetailsResponse } from 'types/api-responses';
import { formatInterest } from 'utils/format-interest';
import { format2 } from 'utils/number';
import { occurrenceKeys } from 'utils/occurrence-keys';
import { EMPTY_CELL } from 'utils/empty-cell';
import classNames from 'classnames';
import { EntityLink } from 'components/entity-link';
import { LinkWithHover } from 'components/hover-card-resource';

/**
 * An acreage to two decimals, an em dash when there is none.
 * @param {number | null} [value] The acreage.
 * @return {string} The formatted acreage.
 */
const acreage = (value?: number | null) => format2(value, EMPTY_CELL);

// The interest columns, in header order.
const STICKS: StickKeys[] = [
  'surface',
  'executive',
  'bonus',
  'delay',
  'royalty',
  'fixedRoyalty',
  'working',
  'override',
  'netRevenueInterest',
];

export type ValueContext = {
  transactionId: number;
  interpretationHref: string;
  documentTitle: string;
};

export type OnSelectedTransaction = React.Dispatch<
  React.SetStateAction<ValueContext | undefined>
>;

// Keyed by the row's key, not its position, so it follows the owner across the
// Changed/All toggle and collapses when the owner leaves the list.
type ExpandedRow =
  { rowKey: string; interest: StickKeys } | { rowKey: null; interest: null };

const COLLAPSED: ExpandedRow = { rowKey: null, interest: null };

type InterestCellProps = {
  cellValue: InterestCalculationDetails | null;
  onClick: () => void;
  onExpandedClick?: () => void;
  changed: boolean;
  interest: InterestCalculationDetails | null;
  setSelectedTransaction: OnSelectedTransaction;
  interpretationId: number;
  expanded: boolean;
} & HighlightRunsheetRowProps;

/**
 * One interest's before/after total, expandable into the math behind it.
 * @param {InterestCellProps} props The cell.
 * @return {JSX.Element} The cell.
 */
function InterestCell({
  cellValue,
  onClick,
  onExpandedClick,
  changed,
  interest,
  setSelectedTransaction,
  refsMap,
  setHighlightedRow,
  interpretationId,
  expanded,
}: InterestCellProps) {
  if (!cellValue) return <>{EMPTY_CELL}</>;
  const cellNode =
    cellValue.total.before !== cellValue.total.after
      ? `${formatInterest(cellValue.total.before)} -> ${formatInterest(
          cellValue.total.after
        )}`
      : formatInterest(cellValue.total.before);

  return (
    <div className={style.relativeCell}>
      <div className={style.alignCell}>
        {cellNode}

        <Button
          hasIconOnly
          kind="ghost"
          size="sm"
          iconDescription="Expand"
          tooltipAlignment="center"
          tooltipPosition="top"
          onClick={onExpandedClick ?? onClick}
          renderIcon={onExpandedClick ? ChevronUp : ChevronDown}
        />
      </div>
      {interest !== null && expanded && (
        <div className={style.popoverDiv}>
          <InterestMath
            interest={interest}
            setSelectedTransaction={setSelectedTransaction}
            refsMap={refsMap}
            setHighlightedRow={setHighlightedRow}
            interpretationId={interpretationId}
            changed={changed}
          />
        </div>
      )}
    </div>
  );
}

type CalculationDetailsProps = {
  titleWorkspace: number;
  titleWorkspaceHref: string;
  referenceLocationHref: string;
  interpretationId: number;
  /** Visual look only: 'minimal' renders the borderless/gray-header Map Query
   *  surface; other consumers stay on 'default', unchanged. */
  variant?: 'default' | 'minimal';
  /** Adds the extra Sub-tract / GIS + legal acreage / lease / royalty-rate /
   *  warnings columns. Used only by the Map Query workspace card. */
  extendedColumns?: boolean;
  /** Render nothing (heading included) on error or when the calculation has no
   *  rows — instead of the empty-state warning. A loading indicator still shows. */
  hideWhenEmpty?: boolean;
  /** Optional heading/section wrapper owned by this component, so it hides
   *  together with the table when hideWhenEmpty and there is no data. */
  heading?: ReactNode;
  headingClassName?: string;
  sectionClassName?: string;
  /** Rendered (with the heading) when the calculation has no rows, instead of
   *  hiding — used by the Map Query workspace card. */
  emptyPlaceholder?: ReactNode;
  /** Reports whether the fetch returned ownership rows, so a consumer can gate
   *  UI (e.g. a Download button) on ownership data actually being present. */
  onDataPresence?: (present: boolean) => void;
} & HighlightRunsheetRowProps;

/**
 * A cell spanning the rows that share its value, sticky as the table scrolls.
 * @param {object} props The cell.
 * @param {number} [props.rowSpan] The rows it spans; unset on the rows it covers.
 * @param {string} [props.className] The sticky wrapper's class.
 * @param {ReactNode} props.children The value.
 * @return {JSX.Element | null} The cell, or nothing on a covered row.
 */
function SpanCell({
  rowSpan,
  className = style.stickyCell,
  children,
}: {
  rowSpan?: number;
  className?: string;
  children: ReactNode;
}) {
  if (!rowSpan) return null;
  return (
    <td rowSpan={rowSpan} className={style.verticalTop}>
      <div className={className}>{children}</div>
    </td>
  );
}

type OwnershipRowProps = {
  row: TabularCalculationDetails;
  rowKey: string;
  extendedColumns: boolean;
  dimmed: boolean;
  expandedRow: ExpandedRow;
  interest: InterestCalculationDetails | null;
  setExpandedRow: (expandedRow: ExpandedRow) => void;
  setSelectedTransaction: OnSelectedTransaction;
  interpretationId: number;
} & HighlightRunsheetRowProps;

/**
 * One owner's interests on a tract and depth, after the transaction.
 * @param {OwnershipRowProps} props The row.
 * @return {JSX.Element} The row.
 */
function OwnershipRow({
  row,
  rowKey,
  extendedColumns,
  dimmed,
  expandedRow,
  interest,
  setExpandedRow,
  setSelectedTransaction,
  interpretationId,
  refsMap,
  setHighlightedRow,
}: OwnershipRowProps) {
  const { document } = row.interpretation;
  const owner = row.entity;
  return (
    <tr className={dimmed ? style.unchangedRow : ''}>
      <SpanCell
        rowSpan={row.transactionRowSpan}
        className={style.stickyCellTitle}
      >
        <LinkWithHover
          kind="document"
          href={`/documents/${document.id}`}
          align="start"
          side="bottom"
          avoidCollisions={false}
          cardContentClassName={style.documentHoverCard}
        >
          <button
            type="button"
            className={style.transactionLink}
            onClick={() =>
              setSelectedTransaction({
                documentTitle: document.documentTitle,
                transactionId: row.transactionId,
                interpretationHref: row.interpretation.href,
              })
            }
          >
            {document.documentTitle} #{row.transactionId}
          </button>
        </LinkWithHover>
        {row.expiration && <p>Lease Termination</p>}
      </SpanCell>
      <SpanCell
        rowSpan={row.tractRowSpan}
        className={classNames(style.stickyCell, style.twoLinesTruncate)}
      >
        {row.tract}
      </SpanCell>
      {extendedColumns && (
        <>
          <SpanCell rowSpan={row.tractRowSpan}>
            {acreage(row.grossAcres)}
          </SpanCell>
          <SpanCell rowSpan={row.tractRowSpan}>
            {acreage(row.legalAcreage)}
          </SpanCell>
        </>
      )}
      <SpanCell rowSpan={row.depthRowSpan}>{row.depth}</SpanCell>
      <td className={style.entityCol}>
        <EntityLink
          id={owner.entity.id}
          name={owner.entity.name}
          hover
          className={style.truncateText}
        />
      </td>
      {STICKS.map((stick) => {
        const expanded =
          expandedRow.rowKey === rowKey && expandedRow.interest === stick;
        return (
          <td key={stick}>
            <InterestCell
              cellValue={owner[stick]}
              onClick={() => setExpandedRow({ rowKey, interest: stick })}
              onExpandedClick={
                expanded ? () => setExpandedRow(COLLAPSED) : undefined
              }
              changed={!!owner[stick]?.changed}
              setSelectedTransaction={setSelectedTransaction}
              refsMap={refsMap}
              setHighlightedRow={setHighlightedRow}
              interpretationId={interpretationId}
              interest={interest}
              expanded={expanded}
            />
          </td>
        );
      })}
      {extendedColumns && (
        <>
          <td>
            {owner.lease?.name ? (
              <Link to={owner.lease.interpretationHref} target="_blank">
                <span className={style.truncateText}>{owner.lease.name}</span>
              </Link>
            ) : (
              EMPTY_CELL
            )}
          </td>
          <td>{owner.lease?.royaltyRate ?? EMPTY_CELL}</td>
        </>
      )}
      <td className={style.entityCol}>
        <ul>
          {row.burdenedEntities.map((entity) => (
            <li key={entity.id}>
              <EntityLink
                id={entity.id}
                name={entity.name}
                className={style.truncateText}
              />
            </li>
          ))}
        </ul>
      </td>
      {extendedColumns && <td>{owner.changed ? 'Ownership changed' : ''}</td>}
    </tr>
  );
}

/**
 * The column headers, the extended ones only when asked.
 * @param {object} props The header.
 * @param {boolean} props.extendedColumns Whether to add the extended columns.
 * @return {JSX.Element} The header.
 */
function OwnershipHeader({ extendedColumns }: { extendedColumns: boolean }) {
  return (
    <thead>
      <tr>
        <th>Transaction</th>
        <th>{extendedColumns ? 'Sub-tract' : 'Tract'}</th>
        {extendedColumns && <th>GIS Gross Acreage</th>}
        {extendedColumns && <th>Legal Acreage</th>}
        <th>Depth</th>
        <th>Entity</th>
        <th>Surface</th>
        <th>Executive</th>
        <th>Bonus</th>
        <th>Delay</th>
        <th>Royalty</th>
        <th>Fixed Royalty</th>
        <th>WI</th>
        <th>ORRI</th>
        <th>NRI</th>
        {extendedColumns && <th>Lease</th>}
        {extendedColumns && <th>Royalty Rate</th>}
        <th>Burdened Entities</th>
        {extendedColumns && <th>Warnings</th>}
      </tr>
    </thead>
  );
}

/**
 * The ownerships a transaction leaves, one row per owner, tract and depth.
 * @param {object} props The table.
 * @return {JSX.Element} The table.
 */
function CalculationDetailsTable({
  titleWorkspace,
  referenceLocationHref,
  refsMap,
  setHighlightedRow,
  interpretationId,
  calculationDetails,
  variant,
  extendedColumns = false,
}: CalculationDetailsProps & {
  calculationDetails: TitleCalculationDetailsResponse[];
}) {
  const [expandedRow, setExpandedRow] = useState<ExpandedRow>(COLLAPSED);
  const [selectedTransaction, setSelectedTransaction] = useState<
    ValueContext | undefined
  >(undefined);

  const hasChangedOwnerships = calculationDetails.some((el) => el.changed);

  const [showOnlyChangedRows, setShowOnlyChangedRows] = useReducer(
    (s) => !s,
    hasChangedOwnerships
  );

  const preparedData = prepareData(calculationDetails, showOnlyChangedRows);
  // An owner can repeat on a tract and depth (another role or lease), so a row
  // is keyed by where it sits and its repeat.
  const rowKeys = occurrenceKeys(
    preparedData.map(
      (row) =>
        `${row.transactionId}|${row.tract}|${row.depth}|${row.entity.entity.id}`
    )
  );

  const expandedIndex = rowKeys.indexOf(expandedRow.rowKey ?? '');
  const interest =
    expandedRow.interest === null || expandedIndex === -1
      ? null
      : preparedData[expandedIndex].entity[expandedRow.interest];

  return (
    <div>
      <InterpretationDrawer
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(undefined)}
        documentTitle={selectedTransaction?.documentTitle || ''}
        interpretationHref={selectedTransaction?.interpretationHref || ''}
        titleWorkspaceId={titleWorkspace}
        referenceLocationHref={referenceLocationHref}
        showEditButton={false}
      />
      {calculationDetails.length !== 0 && (
        <div className={style.alignRight}>
          <Button
            kind="ghost"
            size="md"
            renderIcon={
              variant === 'minimal'
                ? ArrowUpRight
                : showOnlyChangedRows
                  ? DataTable
                  : DataTableReference
            }
            onClick={() => setShowOnlyChangedRows()}
          >{`Show ${
            showOnlyChangedRows ? 'All' : 'Changed'
          } Ownerships`}</Button>
        </div>
      )}
      {calculationDetails.length === 0 ? (
        <InlineNotification
          kind="warning"
          title="This interpretation has no transactions"
          lowContrast
        />
      ) : preparedData.length ? (
        <div className={style.detailsTableWrap}>
          <table
            className={classNames(style.detailsTable, {
              [style.minimal]: variant === 'minimal',
            })}
          >
            <OwnershipHeader extendedColumns={extendedColumns} />
            <tbody>
              {preparedData.map((row, i) => (
                <OwnershipRow
                  key={rowKeys[i]}
                  row={row}
                  rowKey={rowKeys[i]}
                  extendedColumns={extendedColumns}
                  dimmed={hasChangedOwnerships && !row.entity.changed}
                  expandedRow={expandedRow}
                  interest={interest}
                  setExpandedRow={setExpandedRow}
                  setSelectedTransaction={setSelectedTransaction}
                  interpretationId={interpretationId}
                  refsMap={refsMap}
                  setHighlightedRow={setHighlightedRow}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <InlineNotification
          kind="info"
          title="This interpretation does not affect ownerships"
          lowContrast
        />
      )}
    </div>
  );
}

type CalculationDetailsFetcherProps = {
  titleWorkspaceHref: string;
  interpretationId: number;
  hideWhenEmpty?: boolean;
  emptyPlaceholder?: ReactNode;
  heading?: ReactNode;
  headingClassName?: string;
  sectionClassName?: string;
  onDataPresence?: (present: boolean) => void;
  renderTableProp: (
    calculationDetails: TitleCalculationDetailsResponse[]
  ) => JSX.Element;
};

/**
 * Loads an interpretation's calculation details and renders them, or its
 * loading, error or empty state.
 * @param {CalculationDetailsFetcherProps} props The fetch.
 * @return {JSX.Element | null} The details.
 */
function CalculationDetailsFetcher({
  renderTableProp,
  interpretationId,
  titleWorkspaceHref,
  hideWhenEmpty,
  emptyPlaceholder,
  heading,
  headingClassName,
  sectionClassName,
  onDataPresence,
}: CalculationDetailsFetcherProps) {
  const calculationDetailsHref = `${titleWorkspaceHref}/interpretations/${interpretationId}/transactions`;

  const interpretationCalculationDetailsAtom =
    useCreateInterpretationCalculationDetailsAtom(calculationDetailsHref);
  const interpretationCalculationDetails = useAtomValue(
    interpretationCalculationDetailsAtom
  );

  // Report whether the fetch returned ownership rows once it resolves, so a
  // consumer (the workspace card) can hide its Download action when empty.
  useEffect(() => {
    if (!onDataPresence) return;
    const resolved = interpretationCalculationDetails.data;
    if (interpretationCalculationDetails.loading || !resolved) return;
    onDataPresence(
      !('error' in resolved) && Array.isArray(resolved) && resolved.length > 0
    );
  }, [
    interpretationCalculationDetails.loading,
    interpretationCalculationDetails.data,
    onDataPresence,
  ]);

  /**
   * Wraps content in the optional heading/section so the header hides together
   * with the table when there is nothing to show.
   * @param {JSX.Element} content The content.
   * @return {JSX.Element} The content, under its heading when there is one.
   */
  const withHeading = (content: JSX.Element) => {
    if (!heading) return content;
    return (
      <section className={sectionClassName}>
        <h4 className={headingClassName}>{heading}</h4>
        {content}
      </section>
    );
  };

  if (
    interpretationCalculationDetails.data &&
    'error' in interpretationCalculationDetails.data
  ) {
    if (hideWhenEmpty) return null;
    return (
      <InlineNotification
        kind="error"
        title={'Error loading calculation details for this interpretation'}
        lowContrast
      />
    );
  }
  if (interpretationCalculationDetails.loading)
    return <InlineLoading description="Loading calculation details..." />;

  if (!interpretationCalculationDetails.data) return null;

  const data = interpretationCalculationDetails.data;

  // No rows: show the placeholder, hide the section, or fall through to the
  // table's own empty notification, depending on the consumer.
  if (data.length === 0) {
    if (emptyPlaceholder) return withHeading(<div>{emptyPlaceholder}</div>);
    if (hideWhenEmpty) return null;
  }

  return withHeading(renderTableProp(data));
}

/**
 * The ownerships an interpretation's transactions leave.
 * @param {CalculationDetailsProps} props The interpretation.
 * @return {JSX.Element} The ownerships.
 */
function CalculationDetails(props: CalculationDetailsProps) {
  return (
    <CalculationDetailsFetcher
      titleWorkspaceHref={props.titleWorkspaceHref}
      interpretationId={props.interpretationId}
      hideWhenEmpty={props.hideWhenEmpty}
      emptyPlaceholder={props.emptyPlaceholder}
      heading={props.heading}
      headingClassName={props.headingClassName}
      sectionClassName={props.sectionClassName}
      onDataPresence={props.onDataPresence}
      renderTableProp={(calculationDetails) => (
        <CalculationDetailsTable
          {...props}
          calculationDetails={calculationDetails}
        />
      )}
    />
  );
}

export { CalculationDetails };
